import { useEffect, useMemo, useState } from 'react';

import { Box, Button, Stack, Typography } from '@mui/material';
import { ImageIcon } from 'lucide-react';

import {
  type DarkScheme,
  injectHead,
} from '@shared/components/emailBodyDocument';
import { useLocale } from '@shared/hooks/locale';
import { rawDarkPalette } from '@shared/theme/StyleProvider';
import { useIsDarkMode } from '@shared/theme/useIsDarkMode';

// The iframe cannot read the host's CSS variables, so the dark canvas takes
// the theme's resolved paper and primary-text values.
const DARK_SCHEME: DarkScheme = {
  background: rawDarkPalette.white,
  color: rawDarkPalette.highText,
};

// Render unverified portal email HTML inside a sandboxed iframe.
// sandbox="" disables scripts, forms, popups, top-level navigation, and
// same-origin access. The injected CSP additionally blocks remote images
// (tracking pixels) and any other passive remote fetch until the user
// explicitly opts in.
export const EmailBody = ({
  html,
  title = 'Enquiry email body',
  fillHeight = false,
  quillContent = false,
  remoteImagesAllowed = false,
  onAllowRemoteImages,
  allowLinks = false,
}: {
  html: string;
  // Names the iframe for assistive tech. Defaults to the original enquiry
  // wording so the existing caller is unchanged.
  title?: string;
  // Absorb the height the parent has left over instead of standing at a fixed
  // 400px. Opt-in, because it only works where an ancestor chain has a definite
  // height to divide up: in a card inside a scrolling list there is no such
  // height, and a percentage would resolve against `auto` and collapse to zero.
  // The iframe cannot size itself to its CONTENT either way: measuring the
  // inner document needs `allow-same-origin`, which would defeat `sandbox=""`.
  fillHeight?: boolean;
  // The body was authored in the CRM's Quill composer: style it like the
  // composer did (zero block margins, ql-* alignment classes, padding) instead
  // of the neutral defaults meant for inbound portal mail.
  quillContent?: boolean;
  // A consent the host already holds for this body (e.g. given in another
  // view of the same conversation): the images load without asking again.
  remoteImagesAllowed?: boolean;
  // The reader opted in here: the host may remember it for its other views.
  onAllowRemoteImages?: () => void;
  // Links open in a new tab instead of being inert. Only for bodies written
  // inside the tenant (templates): on inbound mail a click would fire the
  // sender's tracker. Scripts, forms and same-origin access stay blocked.
  allowLinks?: boolean;
}) => {
  const { t } = useLocale();
  const [allowRemoteImages, setAllowRemoteImages] =
    useState(remoteImagesAllowed);
  useEffect(() => {
    if (remoteImagesAllowed) {
      setAllowRemoteImages(true);
    }
  }, [remoteImagesAllowed]);
  const isDarkMode = useIsDarkMode();
  // Dark canvas for CRM-authored bodies only (see emailBodyDocument).
  const darkScheme = quillContent && isDarkMode ? DARK_SCHEME : null;
  const srcDoc = useMemo(
    () => injectHead(html, allowRemoteImages, quillContent, darkScheme),
    [html, allowRemoteImages, quillContent, darkScheme],
  );

  return (
    <Stack
      spacing={1}
      sx={
        fillHeight ? { height: '100%', minHeight: 0, width: '100%' } : undefined
      }
    >
      {!allowRemoteImages && (
        <Stack
          sx={{
            flexDirection: 'row',
            // On a narrow pane the row breaks into two clean lines (text, then
            // button) instead of squeezing both into word-per-line columns.
            flexWrap: 'wrap',
            alignItems: 'center',
            gap: 1,
            px: 1,
            py: 0.5,
            borderRadius: 1,
            backgroundColor: 'action.hover',
          }}
        >
          <Typography
            variant="caption"
            sx={{ color: 'text.secondary', flex: '1 1 auto' }}
          >
            {t('Remote images are blocked')}
          </Typography>
          <Button
            size="small"
            variant="text"
            startIcon={<ImageIcon />}
            onClick={() => {
              setAllowRemoteImages(true);
              onAllowRemoteImages?.();
            }}
            sx={{ whiteSpace: 'nowrap', flexShrink: 0 }}
          >
            {t('Load remote images')}
          </Button>
        </Stack>
      )}
      <Box
        sx={
          fillHeight
            ? {
                // Take the leftover height, with a floor tall enough to read a
                // message without scrolling on desktop AND mobile. `resize`
                // gives the reader a native drag handle (bottom-right corner)
                // to enlarge the preview; the dragged inline height becomes the
                // flex basis and the pane scrolls to accommodate it.
                flex: '1 0 auto',
                minHeight: 450,
                resize: 'vertical',
                overflow: 'hidden',
                display: 'flex',
              }
            : { display: 'flex', minHeight: 400 }
        }
      >
        <Box
          component="iframe"
          title={title}
          srcDoc={srcDoc}
          sandbox={
            allowLinks ? 'allow-popups allow-popups-to-escape-sandbox' : ''
          }
          sx={{
            width: '100%',
            flex: 1,
            // The iframe is a separate document with its own canvas, so
            // without an outline it bleeds into the pane and the reader cannot
            // tell where the message ends and the CRM chrome begins. `divider`
            // rather than a hardcoded grey so it tracks the theme.
            border: 1,
            borderColor: 'divider',
            borderRadius: 1,
            // Painted behind the document too, so there is no white flash
            // before the srcDoc's own body background applies.
            background: darkScheme?.background ?? 'white',
            display: 'block',
          }}
        />
      </Box>
    </Stack>
  );
};
